<template>
  <div
    class="modal fade dq-modal"
    id="employee_employment_status_modal"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="false"
    data-bs-keyboard="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-status-body">
          <div class="dq-status-hero">
            <span class="dq-status-badge" :class="isDeactivate ? 'dq-status-badge-warn' : 'dq-status-badge-ok'">
              <i :class="isDeactivate ? 'ti ti-user-off' : 'ti ti-user-check'"></i>
            </span>
          </div>

          <h4 class="mb-2 dq-status-title">{{ title }}</h4>
          <p class="mb-0 dq-status-subtitle">
            <span v-if="isBulk">
              You are about to {{ verb }} <b>{{ pending.count }}</b> selected employee(s).
            </span>
            <span v-else>
              You are about to {{ verb }} <b>{{ pending.subject }}</b>.
            </span>
            Inactive staff cannot use the employee portal.
          </p>

          <div v-if="isDeactivate" class="dq-status-note mt-3">
            <i class="ti ti-info-circle me-2"></i>
            <strong>Administrative deactivate only</strong> — no last working day, leave encashment, or
            automatic cancellation of pending leave. For leavers, use
            <strong>Termination</strong> or <strong>Resignation</strong>.
          </div>

          <div class="d-flex justify-content-center flex-wrap gap-2 mt-4">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal" :disabled="submitting">
              Cancel
            </button>
            <button
              type="button"
              class="btn"
              :class="isDeactivate ? 'btn-warning' : 'btn-primary'"
              :disabled="submitting"
              @click="confirm"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
              {{ confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { Modal } from "bootstrap";

const emit = defineEmits(["confirmed"]);

const submitting = ref(false);

const pending = ref({
  status: "Inactive",
  ids: [],
  subject: "",
  count: 0,
});

const isDeactivate = computed(() => pending.value.status === "Inactive");
const isBulk = computed(() => (pending.value.count || 0) > 1);
const verb = computed(() => (isDeactivate.value ? "deactivate" : "activate"));
const title = computed(() =>
  isDeactivate.value ? "Confirm deactivate" : "Confirm activate",
);
const confirmLabel = computed(() =>
  isDeactivate.value ? "Yes, deactivate" : "Yes, activate",
);

let modalInstance = null;

function getModal() {
  const el = document.getElementById("employee_employment_status_modal");
  if (!el) return null;
  modalInstance = Modal.getOrCreateInstance(el, { backdrop: false, keyboard: true });
  return modalInstance;
}

/**
 * @param {{ status: 'Active'|'Inactive', ids: number[], subject?: string }} payload
 */
function open(payload) {
  const ids = Array.isArray(payload?.ids) ? payload.ids.map((id) => Number(id)).filter(Boolean) : [];
  if (!ids.length) return;

  pending.value = {
    status: payload.status === "Active" ? "Active" : "Inactive",
    ids,
    subject: payload.subject || "this employee",
    count: ids.length,
  };
  submitting.value = false;
  getModal()?.show();
}

function hide() {
  modalInstance?.hide();
}

function confirm() {
  if (submitting.value || !pending.value.ids.length) return;
  emit("confirmed", {
    status: pending.value.status,
    ids: [...pending.value.ids],
  });
}

function setSubmitting(value) {
  submitting.value = !!value;
}

defineExpose({ open, hide, setSubmitting });
</script>

<style scoped>
.dq-status-body {
  padding: 22px 20px 20px;
  text-align: center;
}
.dq-status-hero {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}
.dq-status-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}
.dq-status-badge-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgb(180, 83, 9);
}
.dq-status-badge-ok {
  background: rgba(34, 197, 94, 0.14);
  color: rgb(21, 128, 61);
}
.dq-status-title {
  font-weight: 800;
  letter-spacing: -0.02em;
}
.dq-status-subtitle {
  color: rgba(17, 24, 39, 0.68);
  font-size: 0.92rem;
  line-height: 1.45;
}
.dq-status-note {
  text-align: left;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.12);
  color: rgba(30, 64, 175, 0.95);
  font-size: 0.84rem;
  line-height: 1.4;
}
</style>
